import { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ChevronRight, ChevronLeft, Sparkles, Loader2, Save, Star, Plus, X, Wand2, Users, Headphones } from "lucide-react";
import api, { URGENCIES, getUser } from "@/lib/api";
import PtFormalityHint from "@/components/PtFormalityHint";
import CustomFieldsSection from "@/components/CustomFieldsSection";
import useFieldsConfig from "@/lib/useFieldsConfig";
import { useQualificationTags } from "@/hooks/useQualificationTags";
import { SAMPLE_QI } from "@/lib/sampleData";

const TREATMENTS = ["Sr.", "Sra.", "Sra. D.", "Dr.", "Dra.", "Prof.", "Prof.ª", "Eng.", "Eng.ª", "Padre", "Madre"];

const Field = ({ label, children, required, hint, hintTone = "muted" }) => (
  <label className="block">
    <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-2">
      {label} {required && <span className="text-foreground">*</span>}
    </div>
    {children}
    {hint && (
      <div
        className={
          "mt-1.5 text-[11px] leading-snug " + (
            hintTone === "error" ? "text-[hsl(var(--danger))]" :
            hintTone === "ok"    ? "text-[hsl(var(--success))]" :
                                   "text-muted-foreground"
          )
        }
      >
        {hint}
      </div>
    )}
  </label>
);

const inp = "w-full bg-card border border-border rounded-lg px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-[#3B82F6]/30";

const Chip = ({ selected, onClick, children, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className={`px-3.5 py-1.5 rounded-full text-xs border transition-all ${selected ? "bg-primary text-foreground border-primary" : "bg-transparent text-muted-foreground border-border hover:border-primary/50"}`}>
    {children}
  </button>
);

export default function LeadNew() {
  const nav = useNavigate();
  const { id: editId } = useParams();
  const isEdit = !!editId;
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [office, setOffice] = useState("");   // top-level lead.office, mandatory before create
  // 25/02/2026 — Toggle "Lead de Teste". Qualquer utilizador pode criar
  // um lead marcado como teste — fica fora dos painéis operacionais
  // (DAF, dashboards) e pode ser eliminado por qualquer utilizador
  // depois.
  const [isTest, setIsTest] = useState(false);
  const [data, setData] = useState({
    contact: { name: "", treatment: "", phone: "", email: "", relationship: "", source: "", language: "pt", is_self: false },
    cared_person: { name: "", age: null, sex: "" },
    cared_person_2: { name: "", age: null, sex: "" },
    has_second_beneficiary: false,
    location: { postal_code: "", municipality: "", district: "", address: "" },
    service: { service_types: [], people_count: 1, urgency: "", has_current_caregiver: null, start_date: "", schedule: "", days_per_week: 5, needs: [], notes: "" },
    custom_data: {},
  });
  const cfg = useFieldsConfig("qualificacao_inicial");
  const { visible: V, label: L } = cfg;
  // Dynamic tag lists (Tipos de Serviço + Necessidades) — admins manage
  // via /settings/qualification-tags. Only active tags are shown here.
  const { serviceTypes: SERVICE_TAGS, needs: NEEDS_TAGS, loading: tagsLoading } =
    useQualificationTags();
  const currentUser = getUser();
  const isAdmin = currentUser?.role === "admin";
  const fillWizardSample = () => {
    setData({
      contact: { ...SAMPLE_QI.contact },
      cared_person: { ...SAMPLE_QI.cared_person },
      cared_person_2: { ...SAMPLE_QI.cared_person_2 },
      has_second_beneficiary: true,
      location: { ...SAMPLE_QI.location },
      service: { ...SAMPLE_QI.service },
      custom_data: {},
    });
    toast.success("Exemplo aplicado — reveja e ajuste antes de guardar");
  };

  // Estimativa — pré-preenchida a partir da pricing config activa
  const [pricingCfg, setPricingCfg] = useState(null);
  const [estOptions, setEstOptions] = useState([]); // [{id,name,mode,hourly_price,hours_per_day,days_per_week,total_days,extra_fee,is_primary}]
  const [estMode, setEstMode] = useState("continuidade"); // fallback; user pode alterar em Step 5
  // Service window for pontual estimates — required before send
  const [estServiceStart, setEstServiceStart] = useState("");
  const [estServiceEnd, setEstServiceEnd] = useState("");

  // Load pricing config once
  useEffect(() => {
    api.get("/pricing/config").then(r => setPricingCfg(r.data)).catch(() => {});
  }, []);

  // 20/02/2026 — Postal code → municipality/district auto-fill.
  // The Atendimento only types the CP and we hit
  // ``/api/postal-code/{cp}`` on blur to fill concelho/distrito. Errors
  // are non-blocking (user can still type manually) but we surface them
  // via a small helper text under the field. ``postalLookup`` tracks
  // the loading + last-result state.
  const [postalLookup, setPostalLookup] = useState({ loading: false, error: "", ok: false });
  const _normaliseCp = (v) => {
    // Strip everything except digits; enforce XXXX-XXX shape.
    const digits = (v || "").replace(/\D/g, "").slice(0, 7);
    if (digits.length <= 4) return digits;
    return `${digits.slice(0, 4)}-${digits.slice(4)}`;
  };
  const lookupPostal = async (raw) => {
    const cp = _normaliseCp(raw);
    if (cp.length < 8) {
      setPostalLookup({ loading: false, error: "", ok: false });
      return;
    }
    setPostalLookup({ loading: true, error: "", ok: false });
    try {
      const r = await api.get(`/postal-code/${cp}`);
      const muni = r.data?.municipality || "";
      const dist = r.data?.district || "";
      setData((d) => ({
        ...d,
        location: {
          ...d.location,
          postal_code: cp,
          // Only overwrite when the field is empty — don't clobber manual edits
          municipality: d.location.municipality || muni,
          district: d.location.district || dist,
        },
      }));
      setPostalLookup({ loading: false, error: "", ok: !!(muni || dist) });
    } catch (e) {
      const detail = e.response?.data?.detail || "Código postal não encontrado";
      setPostalLookup({ loading: false, error: detail, ok: false });
    }
  };

  // Auto-derive mode from serviço.service_types (Serviço Pontual vs Continuado)
  useEffect(() => {
    const types = data.service.service_types || [];
    const isPontual = types.some(t => (t || "").toLowerCase().includes("pontual"));
    setEstMode(isPontual ? "pontual" : "continuidade");
    // eslint-disable-next-line
  }, [data.service.service_types]);

  // "Interna" == cuidador permanente na casa → carga diária SEMPRE 24h.
  // Assim que Interna é seleccionado, forçamos o horário para "24h" e
  // desactivamos o campo abaixo. O nº de dias/sem fica livre (5 ou 7,
  // consoante a família).
  const isInternaSelected = (data.service.service_types || [])
    .some(t => (t || "").toLowerCase() === "interna");
  useEffect(() => {
    if (isInternaSelected && data.service.schedule !== "24h") {
      setData(prev => ({
        ...prev,
        service: { ...prev.service, schedule: "24h" },
      }));
    }
    // eslint-disable-next-line
  }, [isInternaSelected]);

  // Seed est options from pricing config — but leave EMPTY. Operator picks rows explicitly,
  // guided by "Recomendadas" chips inferred from service_types.
  useEffect(() => {
    if (!pricingCfg || isEdit) return;
    setEstOptions([]);
    // eslint-disable-next-line
  }, [pricingCfg, isEdit]);

  const activePricingRows = useMemo(
    () => (pricingCfg?.rows || []).filter(r => r.active !== false).sort((a,b) => (a.order||0)-(b.order||0)),
    [pricingCfg]
  );

  const recommendedRowIds = useMemo(() => {
    const types = (data.service.service_types || []).map(t => (t || "").toLowerCase());
    if (!types.length) return new Set();
    // Extract meaningful tokens (≥4 chars) from selected service types
    const tokens = new Set();
    types.forEach(t => {
      t.split(/\s+/).forEach(w => {
        const wc = w.replace(/[^\wáéíóúãõâêôç]/g, "");
        if (wc.length >= 4) tokens.add(wc);
      });
    });
    return new Set(activePricingRows.filter(r => {
      const name = (r.name || "").toLowerCase();
      return [...tokens].some(tok => name.includes(tok) || tok.includes(name));
    }).map(r => r.id));
  }, [data.service.service_types, activePricingRows]);

  const usedIds = useMemo(() => new Set(estOptions.map(o => o.id)), [estOptions]);
  const availableRows = useMemo(() => activePricingRows.filter(r => !usedIds.has(r.id)), [activePricingRows, usedIds]);
  const recommendedRows = useMemo(() => availableRows.filter(r => recommendedRowIds.has(r.id)), [availableRows, recommendedRowIds]);

  const addRowFromPricing = (row) => {
    setEstOptions(prev => [...prev, {
      id: row.id,
      name: row.name || "",
      mode: estMode,
      hourly_price: row.hourly_price || 0,
      hours_per_day: row.hours_per_day || 0,
      days_per_week: row.days_per_week || 0,
      total_days: 0,
      extra_fee: 0,
      is_primary: prev.length === 0, // first added is primary by default
    }]);
  };
  const removeRow = (idx) => {
    setEstOptions(prev => {
      const wasPrimary = prev[idx]?.is_primary;
      const remaining = prev.filter((_, i) => i !== idx);
      if (wasPrimary && remaining.length) remaining[0].is_primary = true;
      return remaining;
    });
  };

  const updateEstOption = (idx, patch) => {
    setEstOptions(prev => prev.map((o, i) => i === idx ? { ...o, ...patch } : o));
  };
  const setPrimary = (idx) => {
    setEstOptions(prev => prev.map((o, i) => ({ ...o, is_primary: i === idx })));
  };
  // When mode toggle changes, propagate to all existing rows
  useEffect(() => {
    setEstOptions(prev => prev.length ? prev.map(o => ({ ...o, mode: estMode })) : prev);
  }, [estMode]);

  // Auto-derive total_days on all pontual rows from the service window.
  const derivedDays = useMemo(() => {
    if (!estServiceStart || !estServiceEnd) return null;
    const s = new Date(estServiceStart);
    const e = new Date(estServiceEnd);
    if (isNaN(s) || isNaN(e) || e < s) return null;
    return Math.round((e - s) / 86400000) + 1;
  }, [estServiceStart, estServiceEnd]);
  useEffect(() => {
    if (derivedDays == null) return;
    setEstOptions(prev => prev.map(o => (
      o.mode === "pontual" ? { ...o, total_days: derivedDays } : o
    )));
  }, [derivedDays]);

  // Live compute per option
  const compute = (o) => {
    const hpd = Number(o.hours_per_day) || 0;
    const price = Number(o.hourly_price) || 0;
    if (o.mode === "pontual") {
      const total_days = Number(o.total_days) || 0;
      const extra = Number(o.extra_fee) || 0;
      const total_hours = +(total_days * hpd).toFixed(2);
      const total_value = +(total_hours * price + extra).toFixed(2);
      return { total_hours, total_value };
    }
    const dpw = Number(o.days_per_week) || 0;
    const weekly_hours = +(dpw * hpd).toFixed(2);
    const weekly_value = +(weekly_hours * price).toFixed(2);
    const monthly_avg = +(weekly_value * 4.33).toFixed(2);
    return { weekly_hours, weekly_value, monthly_avg };
  };

  // Load existing lead when in edit mode
  useEffect(() => {
    if (!isEdit) return;
    api.get(`/leads/${editId}`).then((r) => {
      const q = r.data.qualification || {};
      const nn = (v, fallback = "") => (v === null || v === undefined ? fallback : v);
      setOffice(nn(r.data.office));
      setData({
        contact: {
          name: nn(q.contact?.name), phone: nn(q.contact?.phone), email: nn(q.contact?.email),
          relationship: nn(q.contact?.relationship), source: nn(q.contact?.source),
          language: nn(q.contact?.language, "pt"),
          // 25/02/2026 — Utilizador reportou que o "Tratamento" nunca
          // gravava. Bug: este useEffect reconstruía o objecto ``contact``
          // sem o ``treatment`` (nem o ``is_self``), fazendo com que
          // qualquer valor gravado desaparecesse ao reabrir a edição.
          treatment: nn(q.contact?.treatment),
          is_self: q.contact?.is_self ?? false,
        },
        cared_person: {
          name: nn(q.cared_person?.name), age: q.cared_person?.age ?? null, sex: nn(q.cared_person?.sex),
        },
        cared_person_2: {
          name: nn(q.cared_person_2?.name), age: q.cared_person_2?.age ?? null, sex: nn(q.cared_person_2?.sex),
        },
        has_second_beneficiary: !!q.has_second_beneficiary,
        location: {
          postal_code: nn(q.location?.postal_code), municipality: nn(q.location?.municipality),
          district: nn(q.location?.district), address: nn(q.location?.address),
        },
        service: {
          service_types: q.service?.service_types || [], people_count: q.service?.people_count ?? 1,
          urgency: nn(q.service?.urgency), has_current_caregiver: q.service?.has_current_caregiver ?? null,
          start_date: nn(q.service?.start_date), schedule: nn(q.service?.schedule),
          days_per_week: q.service?.days_per_week ?? 5, needs: q.service?.needs || [],
          notes: nn(q.service?.notes),
        },
        custom_data: q.custom_data || {},
      });
    }).catch(() => toast.error("Erro ao carregar lead"));
  }, [isEdit, editId]);

  const set = (section, field, value) => setData((d) => ({ ...d, [section]: { ...d[section], [field]: value } }));
  const toggle = (section, field, value) => {
    setData((d) => {
      const arr = d[section][field] || [];
      return { ...d, [section]: { ...d[section], [field]: arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value] } };
    });
  };

  // "Guardar e iniciar chamada": cria (ou reaproveita) o lead com o mínimo
  // preenchido, arranca a chamada e leva o operador para o Guião de
  // Atendimento onde continua a preencher os passos com o cronómetro a
  // correr. Pensado para quem atende ao telefone: um clique e já está no
  // guião com o timer da chamada visível.
  const saveAndStartCall = async () => {
    if (!office.trim()) {
      toast.error("Escolha o Escritório de origem antes de iniciar a chamada.");
      return;
    }
    if (!data.contact.name?.trim() || !data.contact.phone?.trim()) {
      toast.error("Preencha o Nome e o Telefone antes de iniciar a chamada.");
      return;
    }
    setLoading(true);
    try {
      let leadId = editId;
      if (!isEdit) {
        const payload = {
          ...data,
          cared_person_2: data.has_second_beneficiary ? data.cared_person_2 : null,
        };
        const qualificationWithContinuity = {
          ...payload,
          service: { ...payload.service, continuity: estMode },
        };
        const { data: created } = await api.post("/leads", {
          qualification: qualificationWithContinuity,
          office,
          is_test: isTest,
        });
        leadId = created.id;
      }
      await api.post(`/leads/${leadId}/call/start`);
      toast.success("Chamada iniciada — continue o guião com o cliente");
      nav(`/leads/${leadId}/script`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao guardar / iniciar chamada");
    } finally {
      setLoading(false);
    }
  };

  const submit = async () => {
    if (!office.trim()) { toast.error("Escolha o Escritório de origem"); return; }
    if (!data.contact.name || !data.contact.phone) { toast.error("Nome e telefone são obrigatórios"); return; }
    if (!(data.contact.treatment || "").trim()) { toast.error("Escolha o tratamento (Sr., Sra., Dr., ...)"); return; }
    if (!(data.location.postal_code || "").trim() || (data.location.postal_code || "").length < 8) {
      toast.error("Código postal obrigatório (formato XXXX-XXX)");
      return;
    }
    // 25/02/2026 — Transportes públicos regulares obrigatório na QI.
    // Pedido do utilizador: garantir que Fase 1 nunca sai sem esta
    // resposta (impacta directamente a decisão de estimativa vs RT).
    const tp = (data.service.transport_public_regular || "").toLowerCase();
    if (tp !== "yes" && tp !== "no") {
      toast.error("Indica se existem transportes públicos regulares para o local do serviço (Passo 4).");
      setStep(4);
      return;
    }
    setLoading(true);
    const payload = {
      ...data,
      cared_person_2: data.has_second_beneficiary ? data.cared_person_2 : null,
    };    try {
      if (isEdit) {
        await api.patch(`/leads/${editId}`, { qualification: payload, office });
        toast.success("Lead atualizado");
        nav(`/leads/${editId}`);
      } else {
        // Persist mode into qualification.service.continuity so backend generates
        // the estimate with the correct mode.
        const qualificationWithContinuity = {
          ...payload,
          service: {
            ...payload.service,
            continuity: estMode,
            // 06/08/2026 — Propagar datas do pontual para o próprio lead
            // (qualification.service.start_date/end_date) — antes eram
            // guardadas só no `estimate.service_start_date`, o que fazia
            // com que ao reeditar o lead a informação parecesse perdida.
            ...(estMode === "pontual" ? {
              start_date: estServiceStart || payload.service?.start_date || null,
              end_date: estServiceEnd || null,
            } : {}),
          },
        };
        const { data: lead } = await api.post("/leads", { qualification: qualificationWithContinuity, office, is_test: isTest });
        toast.success("Qualificação criada. A gerar estimativa...");
        await api.post(`/leads/${lead.id}/estimate`);
        // Overlay user-entered values on top of the auto-generated options
        if (estOptions.length) {
          const primary = estOptions.find(o => o.is_primary) || estOptions[0];
          await api.patch(`/leads/${lead.id}/estimate`, {
            primary_option_id: primary.id,
            options: estOptions.map(o => ({
              id: o.id,
              name: o.name,
              mode: o.mode,
              hourly_price: Number(o.hourly_price) || 0,
              hours_per_day: Number(o.hours_per_day) || 0,
              days_per_week: Number(o.days_per_week) || 0,
              total_days: Number(o.total_days) || 0,
              extra_fee: Number(o.extra_fee) || 0,
            })),
            ...(estMode === "pontual" ? {
              service_start_date: estServiceStart || null,
              service_end_date: estServiceEnd || null,
            } : {}),
          });
        } else if (estMode === "pontual" && (estServiceStart || estServiceEnd)) {
          // No options overlay but service window was filled — persist dates anyway.
          await api.patch(`/leads/${lead.id}/estimate`, {
            service_start_date: estServiceStart || null,
            service_end_date: estServiceEnd || null,
          });
        }
        toast.success("Estimativa em rascunho — podes rever no ecrã do Lead");
        nav(`/leads/${lead.id}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao guardar lead");
    } finally { setLoading(false); }
  };

  const steps = [
    { n: 1, t: "Contacto" },
    { n: 2, t: "Pessoa Cuidada" },
    { n: 3, t: "Localização" },
    { n: 4, t: "Serviço" },
    ...(isEdit ? [] : [{ n: 5, t: "Estimativa" }]),
  ];
  const lastStep = steps.length;

  return (
    <div className="p-8 lg:p-12 max-w-4xl mx-auto" data-testid="lead-new-root">
      <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-semibold mb-2">Fase 1 · Atendimento</div>
      <div className="flex items-start justify-between gap-4 mb-2">
        <h1 className="font-display text-4xl lg:text-5xl tracking-tight">{isEdit ? "Editar Qualificação Inicial" : "Nova Qualificação Inicial"}</h1>
        <div className="flex items-center gap-2 flex-shrink-0 mt-2 flex-wrap justify-end">
          {!isEdit && (
            <button
              type="button"
              onClick={saveAndStartCall}
              disabled={loading || !data.contact.name?.trim() || !data.contact.phone?.trim()}
              data-testid="qi-save-and-start-call"
              title={
                !data.contact.name?.trim() || !data.contact.phone?.trim()
                  ? "Preencha Nome e Telefone para iniciar chamada"
                  : "Guarda o lead com os dados mínimos e inicia a chamada — continua no Guião de Atendimento"
              }
              className="text-[11px] px-3 py-1.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-primary-foreground inline-flex items-center gap-1.5 font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Headphones className="w-3 h-3" /> Guardar e iniciar chamada
            </button>
          )}
          {isAdmin && !isEdit && (
            <button type="button" onClick={fillWizardSample} data-testid="admin-fill-qi"
              className="text-[11px] px-3 py-1.5 rounded-full border border-dashed border-primary text-foreground hover:bg-muted/40 inline-flex items-center gap-1.5">
              <Wand2 className="w-3 h-3" /> Admin · Preencher com exemplo
            </button>
          )}
        </div>
      </div>
      <p className="text-muted-foreground text-sm mb-6">Recolha inicial de informação. A avaliação clínica é feita na fase técnica.</p>

      {!isEdit && (
        <div className="mb-8 rounded-xl border border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning-bg))] px-4 py-3 text-[12px] text-[hsl(var(--warning))]">
          <b>Nota:</b> a criação manual destina-se a leads recebidas por <b>telefone, WhatsApp ou presencialmente</b>.
          Leads originadas em formulários web são importadas automaticamente do Zoho CRM.
        </div>
      )}

      <div className="flex items-center gap-3 mb-10">
        {steps.map((s, i) => (
          <div key={s.n} className="flex items-center flex-1">
            <div className={`flex items-center gap-2 ${step >= s.n ? "text-foreground" : "text-muted-foreground/70"}`}>
              <div className={`w-6 h-6 rounded-full border flex items-center justify-center text-xs font-ui ${step >= s.n ? "border-primary bg-primary/10 text-foreground" : "border-[#CDC5B3]"}`}>{s.n}</div>
              <span className="text-xs uppercase tracking-[0.15em] hidden sm:inline">{s.t}</span>
            </div>
            {i < steps.length - 1 && <div className={`flex-1 h-px mx-3 ${step > s.n ? "bg-primary/50" : "bg-muted"}`} />}
          </div>
        ))}
      </div>

      <div className="bg-card border border-border rounded-2xl p-8 space-y-6">
        {step === 1 && (
          <div className="space-y-5">
            {/* 09/02/2026 — Escritório de origem passou a ser obrigatório
                logo na criação: sem ele o Relatório Diário não consegue
                fatiar leads por escritório e a auto-atribuição não sabe
                para onde encaminhar. Fica destacado no topo do step 1. */}
            <div className={`rounded-2xl border p-5 ${!office.trim() ? "border-primary bg-primary/5" : "border-border bg-card"}`} data-testid="office-required-block">
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex-1 min-w-[220px]">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-1">
                    Escritório de origem <span className="text-[hsl(var(--danger))]">*</span>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Onde é que este lead vai ficar atribuído? Determina para que Relatório Diário fica visível e a auto-atribuição.
                  </div>
                </div>
                <select
                  data-testid="qi-office"
                  value={office}
                  onChange={(e) => setOffice(e.target.value)}
                  required
                  className={`text-sm bg-card border rounded-full px-4 py-2 min-w-[200px] ${!office.trim() ? "border-primary text-foreground" : "border-border text-foreground"}`}
                >
                  <option value="">— escolha o escritório —</option>
                  <option value="Porto">Porto</option>
                  <option value="Braga">Braga</option>
                  <option value="Leiria">Leiria</option>
                  <option value="Algarve">Algarve</option>
                </select>
              </div>
              {!office.trim() && (
                <div className="mt-3 text-[11px] text-[hsl(var(--warning))] font-medium">
                  Escolha um escritório para poder avançar para o próximo passo.
                </div>
              )}
              {/* 25/02/2026 — Toggle "Lead de Teste". Marca este lead como
                  ambiente de teste — fica excluído dos painéis
                  operacionais (DAF, dashboards, alertas) e pode ser
                  eliminado por qualquer utilizador. */}
              <label className="mt-4 flex items-start gap-2.5 text-xs cursor-pointer select-none border-t border-border/60 pt-3">
                <input
                  type="checkbox"
                  checked={isTest}
                  onChange={(e) => setIsTest(e.target.checked)}
                  className="mt-0.5"
                  data-testid="qi-is-test"
                />
                <span>
                  <b className="text-foreground">Lead de Teste</b>
                  <span className="text-muted-foreground"> — fica fora dos painéis operacionais (DAF, dashboards, alertas) e pode ser eliminado por qualquer utilizador. Usa quando estás a explorar o sistema ou a treinar-te.</span>
                </span>
              </label>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label={L("contact.treatment","Tratamento")} required>
              <select data-testid="qi-treatment" className={inp} value={data.contact.treatment || ""} onChange={(e)=>set("contact","treatment",e.target.value)}>
                <option value="">—</option>
                {TREATMENTS.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </Field>
            {V("contact.name") && <Field label={L("contact.name","Nome")} required><input data-testid="qi-name" className={inp} value={data.contact.name} onChange={(e)=>set("contact","name",e.target.value)} /></Field>}
            {V("contact.phone") && <Field label={L("contact.phone","Telefone")} required><input data-testid="qi-phone" className={inp} value={data.contact.phone} onChange={(e)=>set("contact","phone",e.target.value)} /></Field>}
            {V("contact.email") && <Field label={L("contact.email","Email")}><input data-testid="qi-email" type="email" className={inp} value={data.contact.email} onChange={(e)=>set("contact","email",e.target.value)} /></Field>}
            {V("contact.relationship") && <Field label={L("contact.relationship","Relação com a pessoa cuidada")}><input data-testid="qi-relationship" className={inp} placeholder="Filha, marido..." value={data.contact.relationship} onChange={(e)=>set("contact","relationship",e.target.value)} /></Field>}
            {V("contact.source") && <Field label={L("contact.source","Origem")}>
              <select data-testid="qi-source" className={inp} value={data.contact.source} onChange={(e)=>set("contact","source",e.target.value)}>
                <option value="">—</option>
                <option value="Telefone">Telefone</option>
                <option value="WhatsApp">WhatsApp</option>
                <option value="Presencial">Presencial</option>
              </select>
            </Field>}
            <Field label="Idioma preferencial">
              <select data-testid="qi-language" className={inp} value={data.contact.language} onChange={(e)=>set("contact","language",e.target.value)}>
                <option value="pt">Português</option><option value="en">Inglês</option><option value="fr">Francês</option>
              </select>
            </Field>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-6">
            <div>
              <div className="text-[11px] uppercase tracking-[0.18em] text-foreground font-semibold mb-3 flex items-center gap-2">
                <Users className="w-3.5 h-3.5" /> Beneficiário 1
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {V("cared_person.name") && <Field label={L("cared_person.name","Nome (opcional)")}><input data-testid="qi-cp-name" className={inp} value={data.cared_person.name || ""} onChange={(e)=>set("cared_person","name",e.target.value)} /></Field>}
                {V("cared_person.age") && <Field label={L("cared_person.age","Idade aproximada")}><input data-testid="qi-cp-age" type="number" className={inp} value={data.cared_person.age || ""} onChange={(e)=>set("cared_person","age",parseInt(e.target.value)||null)} /></Field>}
                {V("cared_person.sex") && <Field label={L("cared_person.sex","Sexo")}>
                  <select data-testid="qi-cp-sex" className={inp} value={data.cared_person.sex || ""} onChange={(e)=>set("cared_person","sex",e.target.value)}>
                    <option value="">—</option><option value="F">Feminino</option><option value="M">Masculino</option><option value="O">Outro</option>
                  </select>
                </Field>}
              </div>
            </div>

            <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer" data-testid="qi-toggle-b2">
              <input type="checkbox" checked={data.has_second_beneficiary} className="accent-primary w-4 h-4"
                onChange={(e) => setData((d) => ({
                  ...d,
                  has_second_beneficiary: e.target.checked,
                  service: { ...d.service, people_count: e.target.checked ? Math.max(2, d.service.people_count || 2) : 1 },
                }))} />
              <span>São <b>duas pessoas</b> a serem cuidadas na mesma habitação (ex: casal idoso, dois pais).</span>
            </label>

            {data.has_second_beneficiary && (
              <div className="border-t border-border pt-5" data-testid="qi-b2-block">
                <div className="text-[11px] uppercase tracking-[0.18em] text-foreground font-semibold mb-3 flex items-center gap-2">
                  <Users className="w-3.5 h-3.5" /> Beneficiário 2
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <Field label="Nome (opcional)"><input data-testid="qi-cp2-name" className={inp} value={data.cared_person_2.name || ""} onChange={(e)=>set("cared_person_2","name",e.target.value)} /></Field>
                  <Field label="Idade aproximada"><input data-testid="qi-cp2-age" type="number" className={inp} value={data.cared_person_2.age || ""} onChange={(e)=>set("cared_person_2","age",parseInt(e.target.value)||null)} /></Field>
                  <Field label="Sexo">
                    <select data-testid="qi-cp2-sex" className={inp} value={data.cared_person_2.sex || ""} onChange={(e)=>set("cared_person_2","sex",e.target.value)}>
                      <option value="">—</option><option value="F">Feminino</option><option value="M">Masculino</option><option value="O">Outro</option>
                    </select>
                  </Field>
                </div>
              </div>
            )}
          </div>
        )}

        {step === 3 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {V("location.postal_code") && (
              <Field
                label={L("location.postal_code","Código Postal")}
                required
                hint={
                  postalLookup.loading
                    ? "A consultar geoapi.pt…"
                    : postalLookup.error
                      ? postalLookup.error
                      : postalLookup.ok
                        ? "Concelho e distrito preenchidos automaticamente ✓"
                        : "Formato: XXXX-XXX — preenche o concelho e distrito automaticamente."
                }
                hintTone={postalLookup.error ? "error" : postalLookup.ok ? "ok" : "muted"}
              >
                <input
                  data-testid="qi-postal"
                  className={inp}
                  value={data.location.postal_code}
                  placeholder="1200-195"
                  onChange={(e) => {
                    const cp = _normaliseCp(e.target.value);
                    set("location", "postal_code", cp);
                    // Auto-trigger lookup once the full CP is typed
                    if (cp.length === 8) lookupPostal(cp);
                  }}
                  onBlur={(e) => lookupPostal(e.target.value)}
                />
              </Field>
            )}
            {V("location.municipality") && <Field label={L("location.municipality","Concelho")}><input data-testid="qi-municipality" className={inp} value={data.location.municipality} onChange={(e)=>set("location","municipality",e.target.value)} /></Field>}
            {V("location.district") && <Field label={L("location.district","Distrito")}><input data-testid="qi-district" className={inp} value={data.location.district} onChange={(e)=>set("location","district",e.target.value)} /></Field>}
            {V("location.address") && <Field label={L("location.address","Morada (opcional)")}><input data-testid="qi-address" className={inp} value={data.location.address} onChange={(e)=>set("location","address",e.target.value)} /></Field>}
          </div>
        )}

        {step === 4 && (
          <div className="space-y-6">
            {V("service.service_types") && <Field label={L("service.service_types","Tipo de serviço pretendido")}>
              <div className="flex flex-wrap gap-2">
                {tagsLoading && <span className="text-xs text-muted-foreground">A carregar tags…</span>}
                {SERVICE_TAGS.map((t) => <Chip key={t.id || t.value} testid={`qi-service-${t.value}`} selected={data.service.service_types.includes(t.value)} onClick={()=>toggle("service","service_types",t.value)}>{t.label || t.value}</Chip>)}
              </div>
            </Field>}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {V("service.people_count") && <Field label={L("service.people_count","Nº pessoas apoiadas")}>
                <select data-testid="qi-people" className={inp} value={data.service.people_count} onChange={(e)=>set("service","people_count",parseInt(e.target.value))}>
                  <option value={1}>1</option><option value={2}>2</option>
                </select>
              </Field>}
              {V("service.urgency") && <Field label={L("service.urgency","Urgência")}>
                <select data-testid="qi-urgency" className={inp} value={data.service.urgency} onChange={(e)=>set("service","urgency",e.target.value)}>
                  <option value="">—</option>{URGENCIES.map(u=><option key={u.value} value={u.value}>{u.label}</option>)}
                </select>
              </Field>}
              {V("service.has_current_caregiver") && <Field label={L("service.has_current_caregiver","Cuidador atualmente?")}>
                <select data-testid="qi-caregiver" className={inp} value={data.service.has_current_caregiver === null ? "" : String(data.service.has_current_caregiver)} onChange={(e)=>set("service","has_current_caregiver",e.target.value === "" ? null : e.target.value === "true")}>
                  <option value="">—</option><option value="true">Sim</option><option value="false">Não</option>
                </select>
              </Field>}
              {V("service.start_date") && <Field label={L("service.start_date","Data prevista de início")}><input data-testid="qi-start" type="date" className={inp} value={data.service.start_date} onChange={(e)=>set("service","start_date",e.target.value)} /></Field>}
              {V("service.schedule") && <Field label={L("service.schedule","Horário pretendido")}>
                <input
                  data-testid="qi-schedule"
                  className={`${inp} ${isInternaSelected ? "bg-muted cursor-not-allowed" : ""}`}
                  placeholder={isInternaSelected ? "24h (permanente)" : "08h-18h"}
                  value={data.service.schedule}
                  onChange={(e)=>set("service","schedule",e.target.value)}
                  disabled={isInternaSelected}
                  title={isInternaSelected ? "Interna implica serviço permanente 24h/dia — não é editável." : ""}
                />
                {isInternaSelected && (
                  <div className="text-[10px] text-muted-foreground mt-1" data-testid="qi-schedule-interna-hint">
                    Interna = cuidador permanente → carga diária fixa em 24h.
                  </div>
                )}
              </Field>}
              {V("service.days_per_week") && <Field label={L("service.days_per_week","Dias por semana")}><input data-testid="qi-days" type="number" min={1} max={7} className={inp} value={data.service.days_per_week} onChange={(e)=>set("service","days_per_week",parseInt(e.target.value))} /></Field>}
            </div>
            {/* Transportes públicos regulares — 25/02/2026: obrigatório na
                QI (pedido do utilizador). Bloqueia estimativa se "Não". */}
            <div className="rounded-xl border border-border bg-card p-4" data-testid="qi-transport-block">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-1">
                    Transportes públicos regulares <span className="text-[hsl(var(--danger))]">*</span>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Existem transportes públicos regulares para o local do serviço?
                    <span className="ml-2">Marcar <b>"Não"</b> bloqueia envio de estimativa (força Reunião Técnica).</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {[
                    { v: "yes", label: "Sim" },
                    { v: "no", label: "Não" },
                  ].map((opt) => {
                    const active = data.service.transport_public_regular === opt.v;
                    return (
                      <button
                        key={opt.v}
                        type="button"
                        data-testid={`qi-transport-${opt.v}`}
                        onClick={() => set("service", "transport_public_regular", opt.v)}
                        className={`text-xs rounded-full px-4 py-1.5 border transition-colors ${
                          active
                            ? (opt.v === "no"
                                ? "bg-[hsl(var(--danger-bg))] border-[hsl(var(--danger))]/40 text-[hsl(var(--danger))] font-semibold"
                                : "bg-[hsl(var(--success-bg))] border-[hsl(var(--success))]/40 text-[hsl(var(--success))] font-semibold")
                            : "bg-card border-border text-muted-foreground hover:border-primary/50"
                        }`}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              {data.service.transport_public_regular === "no" && (
                <div className="mt-3 text-[11px] text-[hsl(var(--danger))]" data-testid="qi-transport-warning">
                  Sem transportes públicos regulares — o cliente terá de agendar Reunião Técnica antes de receber estimativa.
                </div>
              )}
            </div>
            {V("service.needs") && <Field label={L("service.needs","Necessidades")}>
              <div className="flex flex-wrap gap-2">
                {tagsLoading && <span className="text-xs text-muted-foreground">A carregar tags…</span>}
                {NEEDS_TAGS.map((n) => <Chip key={n.id || n.value} testid={`qi-need-${n.value}`} selected={data.service.needs.includes(n.value)} onClick={()=>toggle("service","needs",n.value)}>{n.label || n.value}</Chip>)}
              </div>
            </Field>}
            {/* Período do serviço PONTUAL — capturado logo no atendimento
                para propagar automaticamente à estimativa (step 5). */}
            {(() => {
              const isPontual = (data.service.service_types || [])
                .some(t => (t || "").toLowerCase().includes("pontual"));
              if (!isPontual) return null;
              return (
                <div className="rounded-xl border border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning-bg))] p-4 space-y-3" data-testid="atendimento-pontual-window">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-primary font-semibold flex items-center gap-2">
                    Período do serviço pontual
                    <span className="text-foreground normal-case tracking-normal font-normal">(obrigatório para serviços pontuais)</span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
                    <label className="block">
                      <span className="block text-[10px] uppercase tracking-[0.15em] text-primary font-semibold mb-1">Data de início</span>
                      <input type="date" value={estServiceStart}
                              onChange={(e)=>setEstServiceStart(e.target.value)}
                              className={inp}
                              data-testid="qi-pontual-start" />
                    </label>
                    <label className="block">
                      <span className="block text-[10px] uppercase tracking-[0.15em] text-primary font-semibold mb-1">Data de fim</span>
                      <input type="date" value={estServiceEnd}
                              min={estServiceStart || undefined}
                              onChange={(e)=>setEstServiceEnd(e.target.value)}
                              className={inp}
                              data-testid="qi-pontual-end" />
                    </label>
                    <div className="text-sm">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-primary font-semibold mb-1">Total de dias</div>
                      <div className="font-mono text-foreground text-lg" data-testid="qi-pontual-days">
                        {derivedDays == null ? "—" : `${derivedDays} dia(s)`}
                      </div>
                    </div>
                  </div>
                  <div className="text-[12px] text-primary">
                    Estas datas propagam automaticamente à estimativa (passo 5) e à proposta.
                  </div>
                </div>
              );
            })()}
            {V("service.notes") && <Field label={L("service.notes","Notas adicionais")}>
              <textarea data-testid="qi-notes" rows={3} className={inp} value={data.service.notes} onChange={(e)=>set("service","notes",e.target.value)} />
              <PtFormalityHint
                text={data.service.notes}
                onFix={(fixed) => set("service", "notes", fixed)}
              />
            </Field>}
            <CustomFieldsSection
              screen="qualificacao_inicial"
              value={data.custom_data}
              onChange={(v)=> setData((d)=>({ ...d, custom_data: v }))}
            />
          </div>
        )}

        {step === 5 && !isEdit && (
          <div className="space-y-6" data-testid="est-step">
            <div className="text-sm text-muted-foreground">
              Introduz os valores da estimativa em rascunho. Depois de guardar, poderás rever, ajustar ou regerar no ecrã do Lead antes de enviar ao cliente.
            </div>

            <Field label="Modo">
              <div className="flex gap-2">
                <Chip testid="est-mode-continuidade" selected={estMode === "continuidade"} onClick={()=>setEstMode("continuidade")}>Continuidade (dias/semana)</Chip>
                <Chip testid="est-mode-pontual" selected={estMode === "pontual"} onClick={()=>setEstMode("pontual")}>Pontual (dias efetivos)</Chip>
              </div>
              <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground mt-2">Auto-derivado dos tipos de serviço — podes alterar aqui.</div>
            </Field>

            {estMode === "pontual" && (
              <div className="rounded-xl border border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning-bg))] p-4 space-y-3" data-testid="est-service-window-new">
                <div className="text-[10px] uppercase tracking-[0.2em] text-primary font-semibold flex items-center gap-2">
                  Período do serviço pontual
                  <span className="text-foreground normal-case tracking-normal font-normal">(obrigatório antes de enviar ao cliente)</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
                  <label className="block">
                    <span className="block text-[10px] uppercase tracking-[0.15em] text-primary font-semibold mb-1">Data de início</span>
                    <input type="date" value={estServiceStart}
                            onChange={(e)=>setEstServiceStart(e.target.value)}
                            className={inp}
                            data-testid="est-new-service-start" />
                  </label>
                  <label className="block">
                    <span className="block text-[10px] uppercase tracking-[0.15em] text-primary font-semibold mb-1">Data de fim</span>
                    <input type="date" value={estServiceEnd}
                            min={estServiceStart || undefined}
                            onChange={(e)=>setEstServiceEnd(e.target.value)}
                            className={inp}
                            data-testid="est-new-service-end" />
                  </label>
                  <div className="text-sm">
                    <div className="text-[10px] uppercase tracking-[0.15em] text-primary font-semibold mb-1">Total de dias</div>
                    <div className="font-mono text-foreground text-lg" data-testid="est-new-service-days">
                      {derivedDays == null ? "—" : `${derivedDays} dia(s)`}
                    </div>
                  </div>
                </div>
                <div className="text-[12px] text-primary">
                  O total de dias é aplicado automaticamente a todas as opções pontuais adicionadas em baixo.
                </div>
              </div>
            )}

            {estOptions.length === 0 && activePricingRows.length === 0 && (
              <div className="bg-[hsl(var(--warning-bg))] border border-[hsl(var(--warning))]/40 text-[hsl(var(--warning))] rounded-lg p-4 text-sm">
                A tabela de preços ainda não tem linhas ativas. A estimativa será gerada em branco — podes editá-la depois no LeadDetail.
              </div>
            )}

            {activePricingRows.length > 0 && (
              <div className="bg-muted/40 border border-primary/40 rounded-2xl p-5 space-y-3" data-testid="est-picker">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-foreground font-semibold mb-1">Adicionar opções à estimativa</div>
                    <div className="text-xs text-muted-foreground">
                      {recommendedRows.length > 0
                        ? <>Baseado nos tipos de serviço selecionados, recomendamos: <b>{recommendedRows.map(r=>r.name).join(", ")}</b></>
                        : "Escolhe as linhas da tabela de preços a incluir nesta estimativa."}
                    </div>
                  </div>
                </div>

                {recommendedRows.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {recommendedRows.map(r => (
                      <button
                        key={r.id}
                        type="button"
                        onClick={() => addRowFromPricing(r)}
                        className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-primary bg-card text-foreground hover:bg-muted font-semibold"
                        data-testid={`est-add-rec-${r.id}`}
                      >
                        <Plus className="w-3 h-3" /> {r.name} <span className="text-[10px] font-normal text-foreground">recomendado</span>
                      </button>
                    ))}
                  </div>
                )}

                {availableRows.length > 0 && (
                  <div className="flex items-center gap-2 pt-2 border-t border-primary/30">
                    <label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">Adicionar outra opção</label>
                    <select
                      className="bg-card border border-border rounded-lg px-3 py-1.5 text-xs text-foreground flex-1 max-w-xs"
                      value=""
                      onChange={e => {
                        const row = availableRows.find(r => r.id === e.target.value);
                        if (row) addRowFromPricing(row);
                      }}
                      data-testid="est-add-picker"
                    >
                      <option value="" disabled>— escolher da tabela —</option>
                      {availableRows.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                    </select>
                  </div>
                )}
                {availableRows.length === 0 && estOptions.length > 0 && (
                  <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground pt-2 border-t border-primary/30">Todas as linhas da tabela já foram adicionadas.</div>
                )}
              </div>
            )}

            {estOptions.length > 0 && (
              <div className="border border-border rounded-2xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted/40 text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                    <tr>
                      <th className="text-left px-3 py-2 w-12">Primária</th>
                      <th className="text-left px-3 py-2">Opção</th>
                      <th className="text-right px-3 py-2">€/hora</th>
                      <th className="text-right px-3 py-2">h/dia</th>
                      {estMode === "pontual" ? (
                        <>
                          <th className="text-right px-3 py-2">Dias efetivos</th>
                          <th className="text-right px-3 py-2">Extra (€)</th>
                          <th className="text-right px-3 py-2 text-foreground">Total (€)</th>
                        </>
                      ) : (
                        <>
                          <th className="text-right px-3 py-2">Dias/sem</th>
                          <th className="text-right px-3 py-2">Valor/sem</th>
                          <th className="text-right px-3 py-2 text-foreground">Média/mês</th>
                        </>
                      )}
                      <th className="w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {estOptions.map((o, i) => {
                      const c = compute(o);
                      return (
                        <tr key={o.id} className={`border-t border-border ${o.is_primary ? "bg-muted/40" : ""}`} data-testid={`est-row-${i}`}>
                          <td className="px-3 py-2">
                            <button type="button" onClick={()=>setPrimary(i)} className={`w-7 h-7 rounded-full border flex items-center justify-center ${o.is_primary ? "bg-primary border-primary text-foreground" : "border-border text-muted-foreground/70 hover:border-primary"}`} data-testid={`est-primary-${i}`}>
                              <Star className="w-3.5 h-3.5" fill={o.is_primary ? "currentColor" : "none"} />
                            </button>
                          </td>
                          <td className="px-3 py-2 font-medium">{o.name}</td>
                          <td className="px-2 py-1"><input type="number" step="0.01" min="0" value={o.hourly_price} onChange={e=>updateEstOption(i,{hourly_price:e.target.value})} className="w-24 text-right bg-card border border-border rounded px-2 py-1 text-sm" data-testid={`est-price-${i}`} /></td>
                          <td className="px-2 py-1"><input type="number" step="0.5" min="0" max="24" value={o.hours_per_day} onChange={e=>updateEstOption(i,{hours_per_day:e.target.value})} className="w-20 text-right bg-card border border-border rounded px-2 py-1 text-sm" data-testid={`est-hpd-${i}`} /></td>
                          {estMode === "pontual" ? (
                            <>
                              <td className="px-2 py-1"><input type="number" step="1" min="0" value={o.total_days} onChange={e=>updateEstOption(i,{total_days:e.target.value})} className="w-24 text-right bg-card border border-border rounded px-2 py-1 text-sm" data-testid={`est-td-${i}`} /></td>
                              <td className="px-2 py-1"><input type="number" step="0.01" min="0" value={o.extra_fee} onChange={e=>updateEstOption(i,{extra_fee:e.target.value})} className="w-24 text-right bg-card border border-border rounded px-2 py-1 text-sm" data-testid={`est-extra-${i}`} /></td>
                              <td className="px-3 py-2 text-right font-semibold text-foreground tabular-nums" data-testid={`est-total-${i}`}>{c.total_value?.toFixed(2)} €</td>
                            </>
                          ) : (
                            <>
                              <td className="px-2 py-1"><input type="number" step="1" min="0" max="7" value={o.days_per_week} onChange={e=>updateEstOption(i,{days_per_week:e.target.value})} className="w-20 text-right bg-card border border-border rounded px-2 py-1 text-sm" data-testid={`est-dpw-${i}`} /></td>
                              <td className="px-3 py-2 text-right text-muted-foreground tabular-nums" data-testid={`est-weekly-${i}`}>{c.weekly_value?.toFixed(2)} €</td>
                              <td className="px-3 py-2 text-right font-semibold text-foreground tabular-nums" data-testid={`est-monthly-${i}`}>{c.monthly_avg?.toFixed(2)} €</td>
                            </>
                          )}
                          <td className="px-2 py-1 text-right">
                            <button type="button" onClick={()=>removeRow(i)} className="w-7 h-7 rounded-full border border-transparent text-muted-foreground/70 hover:text-[hsl(var(--danger))] hover:border-[#F5CACA] flex items-center justify-center" data-testid={`est-remove-${i}`} title="Remover opção">
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            <div className="text-xs text-muted-foreground">Marca uma opção como <b>primária</b> (estrela) — é a que será enviada ao cliente por defeito.</div>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between mt-8">
        <button data-testid="wizard-back" onClick={()=>setStep(Math.max(1, step-1))} disabled={step===1}
          className="flex items-center gap-2 text-muted-foreground hover:text-foreground disabled:opacity-30 text-sm">
          <ChevronLeft className="w-4 h-4" /> Voltar
        </button>
        {step < lastStep ? (
          (() => {
            // Gate Step 1 → Step 2: office must be picked before proceeding.
            const stepIsOffice = step === 1;
            const missingOffice = stepIsOffice && !office.trim();
            // Gate Step 4 → Step 5 for pontual services: dates must be filled.
            const stepIsService = step === 4;
            const isPontualPicked = (data.service.service_types || [])
              .some(t => (t || "").toLowerCase().includes("pontual"));
            const missingWindow = stepIsService && isPontualPicked && (
              !estServiceStart || !estServiceEnd || (derivedDays == null || derivedDays < 1)
            );
            const blocked = missingOffice || missingWindow;
            const goNext = () => {
              if (missingOffice) {
                toast.error("Escolha o Escritório de origem antes de avançar.");
                return;
              }
              if (missingWindow) {
                toast.error("Serviço pontual: preenche data de início e data de fim (a data de fim tem de ser posterior).");
                return;
              }
              setStep(step + 1);
            };
            return (
              <button data-testid="wizard-next" onClick={goNext} disabled={blocked}
                       className={`flex items-center gap-2 rounded-md px-4 py-2 text-sm transition-all border ${
                        blocked
                          ? "bg-muted border-[#EBE0C7] text-foreground cursor-not-allowed opacity-70"
                          : "bg-muted hover:bg-muted border-border"}`}>
                Seguinte <ChevronRight className="w-4 h-4" />
              </button>
            );
          })()
        ) : (
          <button data-testid="wizard-generate" onClick={submit} disabled={loading} className="flex items-center gap-2 bg-foreground hover:bg-slate-800 text-background font-semibold rounded-full px-6 py-3 text-sm transition-all disabled:opacity-60">
            {loading
              ? <><Loader2 className="w-4 h-4 animate-spin" /> {isEdit ? "A guardar" : "A gerar estimativa"}</>
              : (isEdit ? <><Save className="w-4 h-4" /> Guardar alterações</> : <><Sparkles className="w-4 h-4" /> Gerar Estimativa</>)}
          </button>
        )}
      </div>
    </div>
  );
}
